/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// Components
import AccountSetItem from './AccountSetItem';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import HoldingIcon from '@/views/components/common/HoldingIcon';
import ModalV2 from '@/views/components/modals/ModalV2';
import { SecondaryLink } from '@/views/components/buttons';
import { Tooltip } from '@/views/components/tooltips';
import Banner, { BannerVariant } from '@/views/components/common/Banner';

// Enums
import { MAX_ACCOUNT_SETS, AccountSetRowKind } from '@/enums/AccountSets';
import { UNSELECTED_ITEM_INDEX } from './AccountSetRow';

// Types
import AccountSet from '@/types/comparison-account-set';
import ComparisonItem from '@/types/comparison-item';
import ComparisonItemType from '@/types/comparison-item-type';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { COMPARISON_ITEM_TYPES, MODAL_SIZES } from '@/utils/constants';
import { formatLocalizedString, formatMessage, formatMessageWithLocale, LOCALE } from '@/utils/i18n';
import { generateTempId } from '@/utils/random';

const ROW_KIND_ALLOWED_TYPES: Record<AccountSetRowKind, ComparisonItemType[]> = {
  [AccountSetRowKind.CURRENT]: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
  [AccountSetRowKind.RECOMMENDED]: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP],
  [AccountSetRowKind.RECOMMENDED_OPTIONAL]: [COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP, COMPARISON_ITEM_TYPES.BENCHMARK],
};

type ManageAccountSetsModalProps = {
  show: boolean;
  onClose: () => void;
  onSave: (accountSets: AccountSet[]) => void;
  initialAccountSets: AccountSet[];
  comparisonItems: ComparisonItem[];
};

const ManageAccountSetsModal: React.FC<ManageAccountSetsModalProps> = ({ show, onClose, onSave, initialAccountSets, comparisonItems }) => {
  const [accountSets, setAccountSets] = useState<AccountSet[]>(initialAccountSets);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [validationByAccountSetId, setValidationByAccountSetId] = useState<Record<string, { currentMissing: boolean; primaryRecommendedMissing: boolean }>>({});
  const [expandNonceByAccountSetId, setExpandNonceByAccountSetId] = useState<Record<string, number>>({});
  const [validatedAccountSetIds, setValidatedAccountSetIds] = useState<string[]>([]);

  const optionalRecommendationLabels = useSelector((state: ReduxState) => state.auth.user?.appConfig?.comparison?.accountSetRecommendations?.labels);

  useEffect(() => {
    if (!show) return;
    setAccountSets(initialAccountSets);
    setSaveAttempted(false);
    setValidationByAccountSetId({});
    setExpandNonceByAccountSetId({});
    setValidatedAccountSetIds([]);
  }, [show, initialAccountSets]);

  const validateAccountSets = useCallback((sets: AccountSet[]) => {
    const next: Record<string, { currentMissing: boolean; primaryRecommendedMissing: boolean }> = {};

    sets.forEach(set => {
      const hasCurrent = set.current != null && set.current !== UNSELECTED_ITEM_INDEX;
      const primaryRec = set.recommendations.find(r => r.isPrimary) ?? set.recommendations[0];
      const hasPrimaryRecommended = primaryRec != null && primaryRec.itemId != null && primaryRec.itemId !== UNSELECTED_ITEM_INDEX;

      const secondaryRecs = set.recommendations.filter(r => !r.isPrimary);
      const hasSecondaryRecommendations = secondaryRecs.some(r => r.itemId != null && r.itemId !== UNSELECTED_ITEM_INDEX);

      // Determine which specific fields are missing
      let currentMissing = false;
      let primaryRecommendedMissing = false;

      if (!hasCurrent && !hasPrimaryRecommended) {
        currentMissing = true;
        primaryRecommendedMissing = true;
      } else if (hasCurrent && !hasPrimaryRecommended && hasSecondaryRecommendations) {
        primaryRecommendedMissing = true;
      }

      if (currentMissing || primaryRecommendedMissing) {
        next[set._id] = { currentMissing, primaryRecommendedMissing };
      }
    });

    return next;
  }, []);

  useEffect(() => {
    if (!saveAttempted) return;

    const nextValidation = validateAccountSets(accountSets);
    const scoped: Record<string, { currentMissing: boolean; primaryRecommendedMissing: boolean }> = {};

    validatedAccountSetIds.forEach(accountSetId => {
      if (nextValidation[accountSetId]) scoped[accountSetId] = nextValidation[accountSetId]!;
    });

    setValidationByAccountSetId(scoped);
  }, [accountSets, saveAttempted, validateAccountSets, validatedAccountSetIds]);

  const nextDefaultNameIndex = useMemo(() => accountSets.length + 1, [accountSets.length]);

  const selectedComparisonItemIndexes = useMemo(() => {
    const selected = new Set<number>();

    accountSets.forEach(accountSet => {
      if (accountSet.current != null && accountSet.current !== UNSELECTED_ITEM_INDEX) selected.add(accountSet.current);
      accountSet.recommendations.forEach(rec => {
        if (rec.itemId != null && rec.itemId !== UNSELECTED_ITEM_INDEX) selected.add(rec.itemId);
      });
    });

    return selected;
  }, [accountSets]);

  const getSelectItemsForRow = useMemo(
    () =>
      ({ rowKind, selectedItemIndex }: { rowKind: AccountSetRowKind; selectedItemIndex: number }) => {
        const allowedTypes = ROW_KIND_ALLOWED_TYPES[rowKind];

        return comparisonItems
          .map((item, index) => ({ item, index }))
          .filter(({ item, index }) => {
            const isSelectedInThisRow = selectedItemIndex !== UNSELECTED_ITEM_INDEX && index === selectedItemIndex;
            if (isSelectedInThisRow) return true;

            if (selectedComparisonItemIndexes.has(index)) return false;

            return allowedTypes.includes(item.type);
          })
          .map(({ item, index }) => ({
            value: String(index),
            label: (
              <span className="account-set-row-select-option">
                <span className="account-set-row-select-option-icon" aria-hidden>
                  <HoldingIcon type={item.type} color={item.color} size={14} />
                </span>
                <span data-private data-dd-privacy="mask">
                  {formatLocalizedString(item.name)}
                </span>
              </span>
            ),
          }));
      },
    [comparisonItems, selectedComparisonItemIndexes],
  );

  const handleCreateAccountSet = () => {
    if (accountSets.length >= MAX_ACCOUNT_SETS) return;

    const nextAccountSet: AccountSet = {
      _id: generateTempId(),
      name: formatMessage('Comparison.AccountSets.DefaultName', { index: nextDefaultNameIndex }),
      current: UNSELECTED_ITEM_INDEX,
      recommendations: [
        {
          label: {
            [LOCALE.EN_CA]: formatMessageWithLocale('Comparison.AccountSets.Row.Recommended', LOCALE.EN_CA),
            [LOCALE.FR_CA]: formatMessageWithLocale('Comparison.AccountSets.Row.Recommended', LOCALE.FR_CA),
          },
          itemId: UNSELECTED_ITEM_INDEX,
          isPrimary: true,
        },
      ],
    };

    setAccountSets(prev => [...prev, nextAccountSet]);
  };

  const handleSave = () => {
    setSaveAttempted(true);

    const idsAtSaveAttempt = accountSets.map(a => a._id);
    setValidatedAccountSetIds(idsAtSaveAttempt);

    const nextValidation = validateAccountSets(accountSets);
    const hasErrors = Object.keys(nextValidation).length > 0;

    if (hasErrors) {
      const scoped: Record<string, { currentMissing: boolean; primaryRecommendedMissing: boolean }> = {};
      idsAtSaveAttempt.forEach(accountSetId => {
        if (nextValidation[accountSetId]) scoped[accountSetId] = nextValidation[accountSetId]!;
      });

      setValidationByAccountSetId(scoped);
      setExpandNonceByAccountSetId(prev => {
        const next = { ...prev };
        Object.keys(scoped).forEach(accountSetId => {
          next[accountSetId] = (next[accountSetId] ?? 0) + 1;
        });
        return next;
      });
      return;
    }

    setValidationByAccountSetId({});
    setSaveAttempted(false);
    setValidatedAccountSetIds([]);

    onSave(accountSets);
  };

  const handleChangeAccountSet = (nextAccountSet: AccountSet) => {
    setAccountSets(prev => prev.map(a => (a._id === nextAccountSet._id ? nextAccountSet : a)));
  };

  const handleDeleteAccountSet = (accountSetId: string) => {
    setAccountSets(prev => prev.filter(a => a._id !== accountSetId));
  };

  const renderModalBody = () => (
    <div className="manage-account-sets-modal-content" data-testid="manageAccountSetsModal-content">
      <div className="modal-description">
        <p className="description-text" data-testid="manageAccountSetsModal-description">
          {formatMessage('Comparison.AccountSets.Modal.Description')}
        </p>
        <p className="benchmark-note">{formatMessage('Comparison.AccountSets.Modal.BenchmarkNote')}</p>
      </div>

      {saveAttempted && Object.keys(validationByAccountSetId).length > 0 && (
        <div className="manage-account-sets-modal-validation-banner" data-testid="manageAccountSetsModal-validationBanner">
          <Banner variant={BannerVariant.Error} size="slim" showCloseButton={false} text={formatMessage('Comparison.AccountSets.Validation.Banner')} />
        </div>
      )}

      <div className="account-sets-section">
        <div className="account-sets-header">
          <div className="header-info">
            <p className="section-title" data-testid="manageAccountSetsModal-sectionTitle">
              {formatMessage('Comparison.AccountSets.Modal.SectionTitle')}
            </p>
            <p className="section-subtitle" data-testid="manageAccountSetsModal-sectionSubtitle">
              {formatMessage('Comparison.AccountSets.Modal.SectionSubtitle', { count: accountSets.length, max: MAX_ACCOUNT_SETS })}
            </p>
          </div>

          {accountSets.length > 0 && (
            <div className="header-actions">
              <Tooltip
                className="custom-tooltip-v2"
                message={formatMessage('Comparison.AccountSets.Modal.CreateButtonDisabledTooltip')}
                disabled={accountSets.length < MAX_ACCOUNT_SETS}
              >
                <span className="manage-account-sets-modal__tooltip-trigger">
                  <SecondaryLink
                    className="secondary-v2"
                    compact
                    icon="ds-add"
                    disabled={accountSets.length >= MAX_ACCOUNT_SETS}
                    onClick={handleCreateAccountSet}
                  >
                    {formatMessage('Comparison.AccountSets.Modal.CreateButton')}
                  </SecondaryLink>
                </span>
              </Tooltip>
            </div>
          )}
        </div>

        <div className="account-sets-list" data-testid="manageAccountSetsModal-list">
          {accountSets.length === 0 ? (
            <EmptyStateCard
              messagePath="Comparison.AccountSets.EmptyState.Message"
              links={[
                {
                  label: 'Comparison.AccountSets.EmptyState.CreateButton',
                  icon: 'ds-add',
                  onClick: handleCreateAccountSet,
                },
              ]}
            />
          ) : (
            <div className="account-sets-items">
              {accountSets.map(accountSet => (
                <AccountSetItem
                  key={accountSet._id}
                  accountSet={accountSet}
                  comparisonItems={comparisonItems}
                  getSelectItemsForRow={({ rowKind, selectedItemIndex }) => getSelectItemsForRow({ rowKind, selectedItemIndex })}
                  optionalRecommendationLabels={optionalRecommendationLabels}
                  validation={validationByAccountSetId[accountSet._id]}
                  forceExpandNonce={expandNonceByAccountSetId[accountSet._id] ?? 0}
                  onChange={handleChangeAccountSet}
                  onDelete={() => handleDeleteAccountSet(accountSet._id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <ModalV2
      show={show}
      onClose={onClose}
      title="Comparison.AccountSets.Modal.Title"
      modalClassName="manage-account-sets-modal"
      size={MODAL_SIZES.LG}
      defaultFooterButtons={{
        primaryLabelToLocalize: 'Common.Save',
        primaryOnClick: handleSave,
      }}
      body={renderModalBody()}
    />
  );
};

export default ManageAccountSetsModal;
